import { useReducer, useEffect } from 'react'
import { useGlobalState } from '../../../context/GlobalStateContext'

/**
 * Centralized table column-preferences for any grid. Replaces the per-grid
 * collectColumnData / submitColumnData / handleUpdatedColumnsChange boilerplate:
 * load saved column order/width/visibility for `preferencesKey`, merge over the
 * grid's `defaultColumns` (new columns are appended, renderers re-attached), and
 * persist on demand via the shared setUser columnData channel.
 *
 * Usage:
 *   const tablePrefs = useTablePreferences('engines', engineDefaultColumns)
 *   <UniversalDataTable data={data} {...tablePrefs} />
 */
export const useTablePreferences = (
    preferencesKey: string,
    defaultColumns: any[]
) => {
    const { globalState, setUser }: any = useGlobalState()
    const saved =
        globalState?.oupUser?.userPreferences?.dataTableColumns?.[preferencesKey]

    const reducer = (s: any, n: any) => ({ ...s, ...n })
    const [state, setState] = useReducer(reducer, {
        columns: defaultColumns,
        density: 'compact',
    })

    // Saved prefs only persist field/headerName/width/isVisible — re-attach the
    // default column's renderer/valueGetter so custom cells (badges, pills) keep
    // working after a save.
    const withRenderers = (cols: any[]) =>
        cols.map((c: any) => {
            const d = defaultColumns.find((dc: any) => dc.field === c.field)
            return d
                ? { ...c, renderCell: d.renderCell, valueGetter: d.valueGetter }
                : c
        })

    useEffect(() => {
        const savedCols = saved?.columns
        if (Array.isArray(savedCols) && savedCols.length) {
            // keep saved order/width/visibility, drop columns no longer defined
            const merged = savedCols
                .map((sc: any) => {
                    const d = defaultColumns.find(
                        (dc: any) => dc.field === sc.field
                    )
                    return d
                        ? {
                              ...d,
                              width: sc.width ?? d.width,
                              isVisible: sc.isVisible,
                          }
                        : null
                })
                .filter(Boolean)
            // append columns added since the layout was saved
            defaultColumns.forEach((dc: any) => {
                if (!merged.find((m: any) => m.field === dc.field))
                    merged.push({ ...dc, isVisible: true })
            })
            setState({ columns: merged, density: saved?.density || 'standard' })
        } else {
            setState({ columns: defaultColumns, density: 'compact' })
        }
    }, [preferencesKey, JSON.stringify(saved?.columns), saved?.density])

    const onUpdatedColumnsChange = (updatedColumns: any[]) => {
        const cols = withRenderers(
            (updatedColumns || []).map((col: any) => ({
                field: col.field,
                headerName: col.headerName,
                width: col.width,
                isVisible: col.isVisible,
            }))
        )
        setState({ columns: cols })
    }

    const onDensityChange = (density: string) => setState({ density })

    const savePreferences = () =>
        setUser({
            key: preferencesKey,
            value: {
                // strip renderers before persisting (functions aren't serializable)
                columns: state.columns.map((c: any) => ({
                    field: c.field,
                    headerName: c.headerName,
                    width: c.width,
                    isVisible: c.isVisible,
                })),
                density: state.density,
            },
            type: 'columnData',
        })

    return {
        apiColumns: state.columns,
        apiDensity: state.density,
        savePreferences,
        onUpdatedColumnsChange,
        onDensityChange,
    }
}

export default useTablePreferences
